import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl';
import CustomSelect from 'utils/components/CustomSelect';
import { useStateMachine } from 'little-state-machine';
import { updateAction } from 'scenes/ProjectMultiStepForm/store';
import { PLACEHOLDER_MESSAGES_STEP1 } from './constants';
import { getValidationShema } from './constants/validationSchema';
import ProductsSelect from './components/ProductsSelect';
import ApplicationSelect from './components/ApplicationSelect';
import SectorSelect from './components/SectorSelect';
import SegmentSelect from './components/SegmentSelect';
import TooltipField from 'scenes/ProjectMultiStepForm/components/TooltipField';
import { createProject, updateProjectInformation } from './mutation';
import {compose, graphql} from 'react-apollo';
import CountrySelect from './components/CountrySelect';
import CitySelect from './components/CitySelect';
import { getApplicationValues } from './components/ApplicationSelect/constants/applications';
import { BUSINESS_UNIT_VALUES } from './constants/select-values';
import proquinalHistory from 'services/browser-history';

const Step1Form = ({ createProject, updateProjectInformation }) => {
  const intl = useIntl();
  const { state, actions } = useStateMachine({ updateAction });

  const updateField = (setFieldValue) => (e) => {
    const { name, value } = e.target;
    setFieldValue(name, value);
  }

  const initialValues = {
    businessUnit: state?.businessUnit || "",
    projectName: state?.projectName || "",
    products: state?.products || "",
    sector: state?.sector || "",
    segment: state?.segment || "",
    application: state?.application || "",
    applicationByUser: state?.applicationByUser || "",
    countryProject: state?.countryProject || "",
    cityProject: state?.cityProject || "",
    implementationYear: state?.implementationYear
  }

  const getApplicationValueWithTranslations = (applicationValue) => {
    const applicationFinded = 
      getApplicationValues(intl).find(({ label }) => label === applicationValue);
      
    return {
      applicationEn: applicationFinded?.translateEn,
      applicationEs: applicationFinded?.translateEs,
    }
  }

  const redirectToStep2 = () =>
    proquinalHistory.push({
      pathname: '/project/creation-form/step2'
    });
  
  const handleSubmit = (values) => {
    const { 
      projectName,
      businessUnit,
      implementationYear,
      segment, 
      countryProject, 
      cityProject, 
      application, 
      applicationByUser,
      sector,
      products,
      ...rest } = values;

    const finalApplicationValue =
      applicationByUser 
        ? { applicationByUser } 
        : { ...getApplicationValueWithTranslations(application) };

    let input = {
      title: projectName,
      instances: 
        [businessUnit && BUSINESS_UNIT_VALUES(intl.locale).find(({ label }) => label === businessUnit).id],
      products: products.map((item) => item.value),
      marketId: sector,
      segmentId: segment,
      countryId: countryProject[0]?.value,
      cityId: cityProject[0]?.value,
      year: implementationYear,
      ...finalApplicationValue
    }

    const filteredInput = Object.fromEntries(
      Object.entries(input).filter(([key, value]) => value)
    );
    
    if (!state?.isEditProject) {
      return createProject({
        variables: {
          ...filteredInput
        }
      }).then(response => {
        const { projectBasicData } = response.data;
  
        if (projectBasicData?.project) { 
          actions.updateAction({ 
            projectId: projectBasicData?.project.id, 
            ...values, 
            isEditProject: true 
          });
          
          redirectToStep2();
        }
  
      }).catch(err => {
        const {graphQLErrors, netWorkError} = err;
  
        if (graphQLErrors || netWorkError) {
          console.error("Error create Project GraphQL:", graphQLErrors);
          console.error("Error create Project Network:", netWorkError);
        }
      });
    } else {
      return updateProjectInformation({
        variables: {
          projectId: state?.projectId,
          ...filteredInput
        }
      }).then(response => {
        const { updateProjectData } = response.data;
  
        if (updateProjectData?.project) { 
          actions.updateAction({ 
            ...state, 
            ...values, 
            projectId: updateProjectData?.project.id, 
          });

          redirectToStep2();
        }
  
      }).catch(err => {
        const {graphQLErrors, netWorkError} = err;
  
        if (graphQLErrors || netWorkError) {
          console.error("Error update Project GraphQL:", graphQLErrors);
          console.error("Error update Project Network:", netWorkError);
        }
      });
    }
  };
  
  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={getValidationShema(intl)}
      enableReinitialize
    >
      {({ setFieldValue, values: { sector, businessUnit, projectName }, isSubmitting }) => {
        return <Form id="step-1-form" onKeyDown={e => e && e.key === "Enter" && e.preventDefault()}>
          <div className="step-0-form__container d-flex flex-column">
            <section className="col-12 col-lg-12 px-0 mb-3 mb-lg-0">
              <label
                className="step-0-form__label d-block p-0 m-0 mb-lg-1 font-weight-bold"
                htmlFor="businessUnit"
              >
                <FormattedMessage
                  id="ProjectStepForm.step1.field.businessUnit"
                  defaultMessage="Unidad de negocio que gestionó el proyecto"
                />
                {"*"}
              </label>
              <CustomSelect
                config={{
                  name: "businessUnit",
                  initialValue: 
                    businessUnit && BUSINESS_UNIT_VALUES(intl.locale).find(({ label }) => label === businessUnit)?.label,
                  disabled: false,
                  disabledPlaceholder: "",
                  placeholderMessage: intl.formatMessage(PLACEHOLDER_MESSAGES_STEP1.businessUnit)
                }}
                selectValues={BUSINESS_UNIT_VALUES(intl.locale)}
                updateField={setFieldValue}
              />
            </section>
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-6 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="projectName"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step1.field.projectName"
                    defaultMessage="Nombre del proyecto"
                  />
                  {"*"}
                </label>
                <Field
                  id="projectName"
                  name="projectName"
                  className="form-control bg-transparent form-control-placeholder"
                  type="text"
                  value={projectName}
                  placeholder={intl.formatMessage(PLACEHOLDER_MESSAGES_STEP1.projectName)}
                  onChange={updateField(setFieldValue)}
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="projectName"
                />
              </div>
              <div className="col-12 col-6 px-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="products"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step1.field.products"
                    defaultMessage="Producto(s) usado(s) en el proyecto"
                  />
                  {"*"}
                </label>
                <TooltipField 
                  tooltipMessage={(
                    <FormattedMessage 
                      id="ProjectStepForm.tooltip.products"
                      defaultMessage='Elige la(s) referencia(s) exacta(s) del producto usado en el proyecto. Puedes seleccionar dos o más si aplican. {br} Ej.: Valencia, Valencia C5, Valencia C5 Heritage o Valencia Biosense.'
                      values={{
                        br: <br />
                      }}
                    />
                  )} 
                />
                <ProductsSelect name="products" updateField={setFieldValue} />
              </div>
            </div>
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-6 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="sector"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step1.field.sector"
                    defaultMessage="Sector/Mercado"
                  />
                  {"*"}
                </label>
                <SectorSelect name="sector" updateField={setFieldValue} />
              </div>
              <div className="col-12 col-6 px-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="segment"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step1.field.segment"
                    defaultMessage="Segmento"
                  />
                  {"*"}
                </label>
                <SegmentSelect name="segment" updateField={setFieldValue} />
              </div>
            </div>
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-6 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="application"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step1.field.finalApplication"
                    defaultMessage="Uso final"
                  />
                </label>
                <TooltipField 
                  tooltipMessage={(
                    <FormattedMessage 
                      id="ProjectStepForm.tooltip.application"
                      defaultMessage='Especifica dónde fue usado/aplicado el producto Ej: Mobiliario, yate, avión, obra de arte.'
                    />
                  )} 
                />
                <ApplicationSelect name="application" updateField={setFieldValue} />
              </div>
              <div className="col-12 col-6 px-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="countryProject"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step1.field.countryProject"
                    defaultMessage="País donde está el proyecto"
                  />
                  {"*"}
                </label>
                <CountrySelect name={"countryProject"} updateField={setFieldValue} />
              </div>
            </div>
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-6 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="cityProject"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step1.field.cityProject"
                    defaultMessage="Ciudad donde esta el proyecto"
                  />
                </label>
                {"*"}
                <CitySelect name={"cityProject"} updateField={setFieldValue} />
              </div>
              <div className="col-12 col-6 px-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="implementationYear"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step1.field.implementationYear"
                    defaultMessage="Año de implementación del proyecto"
                  />
                </label>
                <Field
                  id="implementationYear"
                  name="implementationYear"
                  className="form-control bg-transparent form-control-placeholder"
                  type="number"
                  placeholder={intl.formatMessage(PLACEHOLDER_MESSAGES_STEP1.implementationYear)}
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="implementationYear"
                />
              </div>
            </div>
            <button
              type="submit"
              className="step-0-form__submit-btn px-5 py-2 mt-4"
              disabled={isSubmitting}
            >
              <FormattedMessage
                id="ProjectStepForm.button.continue"
                defaultMessage="Continuar"
              />
            </button>
          </div>
        </Form>
      }}
    </Formik>
  );
}

export default compose(
  graphql(createProject, { name: "createProject" }),
  graphql(updateProjectInformation, { name: "updateProjectInformation" })
)(Step1Form);